/**
 * Engineering — analyze issues, understand code context, propose patches,
 * run checks and prepare PR/MR workflows.
 *
 * Built on shipped functionality: missions + orchestration engine, sandbox
 * runtime verification, tiered patch review, and PR-only delivery through
 * the integration outbox. Auto-start on intake is the existing mig-169
 * behavior for non-support-desk projects; every external write stays behind
 * its approval gate and the outbox.
 */
import type { UseCaseDefinition } from "../types";

export const engineeringUseCase: UseCaseDefinition = {
  id: "engineering",
  version: 1,
  labelKey: "useCases.engineering.label",
  shortDescriptionKey: "useCases.engineering.short",
  fullDescriptionKey: "useCases.engineering.full",
  primaryOutcomeKey: "useCases.engineering.outcome",
  availability: "available",
  applicableWorkProfiles: ["engineering", "release_devops", "manager_reviewer", "admin", "custom"],
  requiredCapabilities: ["code.read", "sandbox.analyze"],
  recommendedCapabilities: [
    "delivery.pr",
    "knowledge.answer",
    "approvals.route",
    "notifications.post",
  ],
  suggestedBindingPurposes: ["code", "delivery", "notifications", "approvals"],
  defaultExecutionMode: "sandbox",
  defaultRemediationStartMode: "automatic_on_intake",
  onboardingStepIds: [
    "engineering.select-issue",
    "engineering.read-only-analysis",
    "engineering.codebase-readiness",
  ],
  dashboardSectionIds: [
    "engineering.issues",
    "engineering.missions",
    "engineering.code",
    // The work sessions the agent runs in, and the patches they produce: on an
    // engineering project these are what you come to the project FOR, not a
    // detour to the tenant-wide queue.
    "engineering.workspaces",
    "engineering.patches",
    "shared.approvals",
  ],
  readinessCheckIds: [
    "engineering.codebase",
    "engineering.code-access",
    "engineering.sandbox",
    // Whether THIS repository has a proved-runnable environment, as opposed to
    // whether the deployment has a sandbox at all
    // (docs/PLAN-generic-environment-discovery.md §10 row I).
    "engineering.environment",
    "engineering.approval-route",
    "engineering.pr-provider",
  ],
  defaultGovernanceProfileId: "balanced",
  requiredPlanFeature: "engineering",
  prerequisiteDescriptionKeys: [
    "useCases.engineering.prereq.codebase",
    "useCases.engineering.prereq.knowledge",
  ],
};
